import { useQuery } from '@apollo/client';
import { Download, Folder as MuiFolderIcon, SwapVert } from '@mui/icons-material';
import { Divider, Menu, type MenuProps } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteContent, useMoveContent } from '~/api/content';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { MoreAction, MoreActionLink, MoreDeleteAction } from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { MoreActionProps } from '~/hooks/table';
import { assert } from '~/lib/assert';
import { ContentMoveDocument } from '../../../components';
import { ReplaceMediaItemDialog } from './';
import type { MediaItemMenuProps__MediaItem } from './MediaItemMenu.generated';

/* GraphQL */ `#graphql
fragment MediaItemMenuProps__MediaItem on MediaItem {
  canDestroy {
    ...AuthorizationResult
  }
  contentItemCount
  designCount
  id
  kind
  name
  originalFile {
    downloadUri
    id
    originalFilename
  }
  status
}
`;

export interface MediaItemMenuProps extends MenuProps {
  context: { id: number } | undefined;
  item: MediaItemMenuProps__MediaItem;
  moreActionProps: MoreActionProps;
  onClose: () => void;
  onReplace: () => void;
}

export const MediaItemMenu = ({
  context,
  item,
  moreActionProps,
  onClose,
  onReplace,
  ...props
}: MediaItemMenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [moveMediaItemDialogOpen, setMoveMediaItemDialogOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [deleteContent] = useDeleteContent();
  const [moveContent, moveContentInfo] = useMoveContent();
  const navigate = useNavigate();

  const { data: contentMoveData } = useQuery(ContentMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveMediaItemDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      contentMoveData?.network?.contentFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [contentMoveData],
  );

  return (
    <>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={onClose}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        {...props}
      >
        <MoreActionLink
          Icon={Download}
          download={item.originalFile.originalFilename}
          to={item.originalFile.downloadUri}
          title={t('common.buttons.download')}
        />
        <MoreAction
          {...moreActionProps}
          Icon={SwapVert}
          disabled={item.status !== 'READY'}
          onClick={() => {
            assert(context != null, 'media: no context');
            setReplaceOpen(() => true);
          }}
          title={t('pages.content.media_details.menu.replace')}
          tooltip={
            item.status !== 'READY' ? t('pages.content.media_details.menu.replace_disabled') : ''
          }
        />
        <MoreAction
          Icon={MuiFolderIcon}
          onClick={() => {
            assert(context != null, 'media: no context');
            setMoveMediaItemDialogOpen(true);
            onClose();
          }}
          title={t('pages.content.media_details.menu.move_to')}
          {...moreActionProps}
        />
        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          disabled={!item.canDestroy.value}
          onClick={async () => {
            assert(context != null);
            if (!(await confirmDelete())) return;

            await deleteContent({
              variables: { items: [{ contentableId: context.id, contentableType: 'MEDIA' }] },
            });
            onClose();
            navigate('..');
          }}
          tooltip={item.canDestroy.reasons?.fullMessages.join(',')}
        />
      </Menu>
      <MoveToFolderDialog
        editableFolderIds={editableFolderIds}
        onSubmit={async (targetFolderId) => {
          await moveContent({
            refetchQueries: ['MediaItemView'],
            variables: {
              contentFolderId: targetFolderId,
              items: [
                {
                  contentableId: item.id,
                  contentableType: 'MEDIA',
                },
              ],
            },
          });
        }}
        loading={moveContentInfo.loading}
        onClose={() => setMoveMediaItemDialogOpen(false)}
        open={moveMediaItemDialogOpen}
        i18nKey="pages.content.dialogs.move_content.title"
      />
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.content.media_details.menu.delete_confirm')}
        deleteConfirm
        prompt={t('pages.content.media_details.menu.delete_prompt')}
        title={t('pages.content.media_details.menu.delete_title')}
      />
      <ReplaceMediaItemDialog
        appCount={item.contentItemCount}
        designCount={item.designCount}
        item={item}
        onClose={() => setReplaceOpen(() => false)}
        onReplace={onReplace}
        open={replaceOpen}
      />
    </>
  );
};
